import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Button, Form, Modal, Spinner} from 'react-bootstrap';
import {apiService} from '../services/api';
import {AppContext} from '../contexts/AppContext';
import {storage} from '../utils/localStorage';
import {isClientAdmin} from '../utils/roles';
import {StatisticView} from '../components/StatisticView';
import {StatisticRecordPicker} from '../components/StatisticRecordPicker';
import {IconPlus, IconSend} from '@tabler/icons-react';
import {StatisticChartDef, StatisticFilterDef, StatisticWidgetDef} from '../types';
import {DATE_TOKENS, isDateToken} from '../utils/statisticDateTokens';
import {initialStatisticFilterValues} from '../utils/statisticValues';
import {StatisticWidgetView} from '../components/StatisticWidgetView';
import {WidgetSkeleton} from '../components/WidgetSkeleton';
import {ConfirmModal} from '../components/ConfirmModal';
import {StatisticWidgetEditorModal, WIDGET_TYPES} from '../components/StatisticWidgetEditorModal';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import './StatisticBuilderPage.css';


interface ChatMessage {
    role: 'user' | 'assistant';
    text: string;
}

interface BuilderFilter {
    code: string;
    displayName: string;
    type: string;
    defaultValue: string | null;
    required: boolean;
    /** false = viewers cannot change it, the default always applies (missing from an older api = true) */
    editable?: boolean;
    targetScreen: string | null;
    targetScreenName: string | null;
    /** Screens a user / many-to-one filter can pick its value through (missing from an older api). */
    pickerScreens?: PickerScreen[];
}

interface Definition {
    id: string;
    displayName: string;
    description: string | null;
    sortOrder: number;
    projectId: string | null;
    userGroups: string[];
    filters: BuilderFilter[];
    /** Widgets users pin to their home page; x/y/columns pick from `columns` (result column names). */
    widgets: StatisticWidgetDef[];
    /** The assistant's proposed visualisation — prefills a new widget. */
    suggestedWidget: StatisticChartDef | null;
    columns: string[];
    chat: ChatMessage[];
    sqlStatus: {valid: boolean; error: string | null};
}

/**
 * Widgets of the statistic as cards with a small live preview; creating and
 * editing happens in the widget editor modal, deleting after a confirmation.
 */
const WidgetList: React.FC<{
    statisticId: string;
    statisticName: string;
    widgets: StatisticWidgetDef[];
    filters: StatisticFilterDef[];
    onDelete: (widget: StatisticWidgetDef) => void;
    onChanged: (definition: Definition) => void;
}> = ({statisticId, statisticName, widgets, filters, onDelete, onChanged}) => {
    // undefined = modal closed, null = new widget
    const [editing, setEditing] = useState<string | null | undefined>(undefined);
    const values = useMemo(() => initialStatisticFilterValues(filters), [filters]);
    const missing = filters.filter(filter => filter.required && !values[filter.code]);
    const {data, isLoading} = useQuery({
        queryKey: ['statisticData', statisticId, values],
        queryFn: ({signal}) => apiService.getStatisticData(statisticId, values, signal),
        enabled: widgets.length > 0 && missing.length === 0,
    });

    return (
        <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="mb-0">Widgety <span className="text-muted small">(používatelia si ich pripínajú na domovskú stránku)</span></Form.Label>
                <Button size="sm" variant="outline-primary" onClick={() => setEditing(null)}>
                    <IconPlus size={14} className="me-1"/>Pridať widget
                </Button>
            </div>
            {widgets.length === 0 ? (
                <div className="text-muted small">Štatistika zatiaľ nemá widget.</div>
            ) : (
                <div className="builder-widget-list">
                    {widgets.map(widget => (
                        <div key={widget.id} className="builder-widget-card">
                            <div className="builder-widget-card-head">
                                <strong>{widget.displayName || statisticName}</strong>
                                <span className="text-muted small">{WIDGET_TYPES.find(type => type.value === widget.type)?.label}</span>
                            </div>
                            <div className="builder-widget-card-preview">
                                {missing.length > 0 ? (
                                    <span className="text-muted small">Náhľad potrebuje predvolené hodnoty filtrov.</span>
                                ) : isLoading ? (
                                    <WidgetSkeleton type={widget.type}/>
                                ) : (
                                    <StatisticWidgetView widget={widget} columns={data?.columns ?? []} rows={data?.rows ?? []}
                                                         height={widget.type === 'kpi' ? 70 : 120}/>
                                )}
                            </div>
                            <div className="d-flex justify-content-end gap-2">
                                <Button size="sm" variant="outline-danger" onClick={() => onDelete(widget)}>Zmazať</Button>
                                <Button size="sm" variant="outline-primary" onClick={() => setEditing(widget.id)}>Upraviť</Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <StatisticWidgetEditorModal show={editing !== undefined} statisticId={statisticId} widgetId={editing ?? null}
                                        onHide={() => setEditing(undefined)}
                                        onSaved={updated => { setEditing(undefined); onChanged(updated as Definition); }}/>
        </div>
    );
};

/** Default of a date filter: a relative period (resolved on every visit) or a fixed day. */
const DateDefaultInput: React.FC<{value: string | null; onChange: (value: string | null) => void}> = ({value, onChange}) => {
    const relative = isDateToken(value);
    const mode = !value ? '' : relative ? value : '@custom';
    return (
        <div className="d-flex gap-1">
            <Form.Select size="sm" value={mode} onChange={e => {
                const next = e.target.value;
                onChange(next === '' ? null : next === '@custom' ? (relative ? null : value) : next);
            }}>
                <option value="">— bez predvolenej —</option>
                {DATE_TOKENS.map(item => <option key={item.token} value={item.token}>{item.label}</option>)}
                <option value="@custom">Konkrétny dátum…</option>
            </Form.Select>
            {mode === '@custom' || (!relative && value) ? (
                <Form.Control size="sm" type="date" value={relative ? '' : value ?? ''} onChange={e => onChange(e.target.value || null)}/>
            ) : null}
        </div>
    );
};

/** Multi-select in the app's relation-picker style (badge box, same as FilterGroup). */
const GroupsPicker: React.FC<{
    all: any[];
    selected: string[];
    onChange: (ids: string[]) => void;
}> = ({all, selected, onChange}) => {
    const [open, setOpen] = useState(false);
    const boxRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const close = (event: MouseEvent) => {
            if (boxRef.current && !boxRef.current.contains(event.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, []);

    const label = (group: any) => group.name || group.displayName || group.code;
    const remaining = all.filter(group => !selected.includes(String(group.id)));

    return (
        <div ref={boxRef} style={{position: 'relative'}}>
            <div
                className="form-select d-flex align-items-center gap-1 flex-wrap"
                style={{cursor: 'pointer', backgroundImage: 'none', paddingRight: '0.5rem', minHeight: '38px'}}
                onClick={() => setOpen(o => !o)}
                role="button"
                tabIndex={0}
            >
                {selected.length === 0 && (
                    <span className="text-muted small d-inline-flex align-items-center gap-1">
                        <IconPlus size={14}/> Vybrať...
                    </span>
                )}
                {selected.map(id => {
                    const group = all.find(g => String(g.id) === id);
                    return (
                        <span key={id}
                              className="badge bg-secondary-subtle text-secondary-emphasis border d-inline-flex align-items-center gap-1 flex-shrink-0"
                              style={{fontWeight: 500, padding: '0.15em 0.45em', lineHeight: 1.3}}>
                            {group ? label(group) : id}
                            <span role="button" aria-label="Odstrániť"
                                  style={{cursor: 'pointer', lineHeight: 1, fontSize: '0.95rem'}}
                                  onClick={e => {
                                      e.stopPropagation();
                                      onChange(selected.filter(s => s !== id));
                                  }}>×</span>
                        </span>
                    );
                })}
                {selected.length > 0 && <IconPlus size={16} className="text-muted ms-auto"/>}
            </div>
            {open && remaining.length > 0 && (
                <div className="card shadow-sm"
                     style={{position: 'absolute', zIndex: 1050, top: '100%', left: 0, right: 0, marginTop: 2, maxHeight: 220, overflowY: 'auto'}}>
                    {remaining.map(group => (
                        <button type="button" key={group.id} className="dropdown-item text-start"
                                onClick={() => onChange([...selected, String(group.id)])}>
                            {label(group)}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const FILTER_TYPE_LABELS: Record<string, string> = {
    string: 'Text',
    integer: 'Celé číslo',
    boolean: 'Áno/Nie',
    date: 'Dátum',
    user: 'Používateľ',
    'many-to-one': 'Záznam z tabuľky',
};

interface PickerScreen {
    id: string;
    displayName: string;
    projectName: string | null;
}

/**
 * "Používateľ · cez screen Programátori · zmeniť": the screen the filter's
 * picker opens, changeable on demand. Only screens in the user's applications
 * are offered - the picker finds its screen there, any other would never load.
 */
const FilterScreenLine: React.FC<{filter: BuilderFilter; onChange: (screen: PickerScreen) => void}> = ({filter, onChange}) => {
    const {applications} = useContext(AppContext);
    const [editing, setEditing] = useState(false);
    const inApps = new Set(applications.flatMap((app: any) => (app.screens || []).map((screen: any) => String(screen.id))));
    const options = (filter.pickerScreens ?? []).filter(screen => inApps.has(screen.id) || screen.id === filter.targetScreen);
    const typeLabel = FILTER_TYPE_LABELS[filter.type] ?? filter.type;
    const currentProject = options.find(screen => screen.id === filter.targetScreen)?.projectName;
    // Screens grouped by project, in the api's order (project, then name)
    const groups = options.reduce<{project: string; screens: PickerScreen[]}[]>((acc, screen) => {
        const project = screen.projectName ?? 'Bez projektu';
        const group = acc.find(g => g.project === project);
        if (group) group.screens.push(screen); else acc.push({project, screens: [screen]});
        return acc;
    }, []);

    if (editing) {
        return (
            <div className="d-flex align-items-center gap-2 mt-1">
                <span className="text-muted small text-nowrap">{typeLabel} · cez screen</span>
                <Form.Select size="sm" autoFocus aria-label="Screen na výber" value={filter.targetScreen ?? ''}
                             onChange={e => {
                                 const screen = options.find(option => option.id === e.target.value);
                                 if (screen) onChange(screen);
                                 setEditing(false);
                             }}
                             onBlur={() => setEditing(false)}>
                    {!filter.targetScreen && <option value="">— vyber screen —</option>}
                    {groups.map(group => (
                        <optgroup key={group.project} label={group.project}>
                            {group.screens.map(screen => (
                                <option key={screen.id} value={screen.id}>
                                    {screen.displayName}{inApps.has(screen.id) ? '' : ' (nie je v aplikáciách)'}
                                </option>
                            ))}
                        </optgroup>
                    ))}
                </Form.Select>
            </div>
        );
    }

    return (
        <div className="text-muted small mt-1">
            {typeLabel}
            {filter.targetScreenName ? ` · cez screen ${filter.targetScreenName}` : ''}
            {filter.targetScreenName && currentProject ? ` (${currentProject})` : ''}
            {options.length > 0 && (
                <>
                    {' · '}
                    <button type="button" className="btn btn-link btn-sm p-0 align-baseline small" onClick={() => setEditing(true)}>
                        zmeniť
                    </button>
                </>
            )}
        </div>
    );
};

/** The default in the same control the statistic's filter panel shows for this type. */
const FilterDefaultInput: React.FC<{filter: BuilderFilter; onChange: (value: string | null) => void}> = ({filter, onChange}) => {
    const value = filter.defaultValue ?? '';
    switch (filter.type) {
        case 'date':
            return <DateDefaultInput value={filter.defaultValue} onChange={onChange}/>;
        case 'user':
        case 'many-to-one':
            return (
                <StatisticRecordPicker
                    filter={{code: filter.code, type: filter.type, targetScreenId: filter.targetScreen}}
                    value={value}
                    onChange={next => onChange(next || null)}
                    currentUserAsToken
                />
            );
        case 'boolean':
            return (
                <Form.Select size="sm" value={value} onChange={e => onChange(e.target.value || null)}>
                    <option value="">—</option>
                    <option value="1">Áno</option>
                    <option value="0">Nie</option>
                </Form.Select>
            );
        case 'integer':
            return <Form.Control size="sm" type="number" value={value} onChange={e => onChange(e.target.value || null)}/>;
        default:
            return <Form.Control size="sm" value={value} onChange={e => onChange(e.target.value || null)}/>;
    }
};

/** Unwraps the api wrapper ({success, data: rawBody}) into the raw body. */
const unwrap = (response: any): {ok: boolean; data?: any; message?: string} => {
    if (!response.success) {
        return {ok: false, message: response.message || 'Nastala chyba.'};
    }
    const body = response.data;
    if (body?.success === false) {
        return {ok: false, message: body.message || 'Nastala chyba.'};
    }
    return {ok: true, data: body?.data ?? body};
};

/**
 * AI statistic builder for client admins. Mirrors the admin chat builder,
 * but the SQL itself never reaches this page — the AI owns it.
 */
export const StatisticBuilderPage: React.FC = () => {
    const {statisticId} = useParams();
    const navigate = useNavigate();

    const [definition, setDefinition] = useState<Definition | null>(null);
    const queryClient = useQueryClient();
    // A definition from the server may carry new SQL: the preview and the widgets
    // must not keep showing the previous result from the cache
    const applyDefinition = (next: Definition) => {
        setDefinition(next);
        queryClient.invalidateQueries({queryKey: ['statisticData', next.id]});
    };
    const [widgetToDelete, setWidgetToDelete] = useState<StatisticWidgetDef | null>(null);
    const [loading, setLoading] = useState(!!statisticId);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);

    const [chatInput, setChatInput] = useState('');
    // Create-mode draft — the AI only fills what the user left blank
    const [draft, setDraft] = useState({displayName: '', description: '', sortOrder: 0, userGroups: [] as string[]});
    const [sending, setSending] = useState(false);
    const chatEndRef = useRef<HTMLDivElement | null>(null);

    const [userGroups, setUserGroups] = useState<any[]>([]);
    const [saving, setSaving] = useState(false);

    const [showPreview, setShowPreview] = useState(false);
    const [showDelete, setShowDelete] = useState(false);

    const canManage = isClientAdmin();

    const invalidateApps = () => storage.removeItem('applicationsCache');

    useEffect(() => {
        if (!canManage) return;
        apiService.getUserGroups().then(r => {
            const u = unwrap(r);
            if (u.ok) setUserGroups(Array.isArray(u.data) ? u.data : []);
        });
    }, [canManage]);

    const loadDefinition = useCallback(async () => {
        if (!statisticId) return;
        setLoading(true);
        const u = unwrap(await apiService.getStatisticDefinition(statisticId));
        if (u.ok) {
            setDefinition(u.data);
        } else {
            setError(u.message || null);
        }
        setLoading(false);
    }, [statisticId]);

    useEffect(() => {
        if (statisticId && !definition) {
            loadDefinition();
        }
    }, [statisticId, definition, loadDefinition]);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({behavior: 'smooth'});
    }, [definition?.chat.length, sending]);

    if (!canManage) {
        return (
            <div className="page-body"><div className="container-xl py-4">
                <div className="alert alert-danger">Na správu štatistík potrebuješ rolu administrátora klienta.</div>
            </div></div>
        );
    }

    const send = async () => {
        const message = chatInput.trim();
        if (message === '' || sending) return;
        setSending(true);
        setError(null);

        const response = definition
            ? await apiService.chatStatistic(definition.id, message)
            : await apiService.createStatistic(message, draft);
        const u = unwrap(response);

        if (!u.ok) {
            setError(u.message || null);
        } else {
            setChatInput('');
            applyDefinition(u.data);
            invalidateApps();
            if (!definition) {
                navigate(`/statistics/${u.data.id}/edit`, {replace: true});
            }
        }
        setSending(false);
    };

    const save = async () => {
        if (!definition) return;
        setSaving(true);
        setError(null);
        const u = unwrap(await apiService.saveStatistic(definition.id, {
            displayName: definition.displayName,
            description: definition.description,
            sortOrder: definition.sortOrder,
            userGroups: definition.userGroups,
            filters: definition.filters.map(f => ({
                code: f.code,
                displayName: f.displayName,
                defaultValue: f.defaultValue,
                required: f.required,
                editable: f.editable !== false,
                targetScreen: f.targetScreen,
            })),
        }));
        if (u.ok) {
            applyDefinition(u.data);
            setNotice('Štatistika bola uložená.');
            invalidateApps();
            setTimeout(() => setNotice(null), 3000);
        } else {
            setError(u.message || null);
        }
        setSaving(false);
    };

    // The preview renders the REAL StatisticView (same as the app), fed with a
    // screen object shaped like StatisticProvider::serializeAsScreen().
    const previewScreen = definition ? ({
        id: definition.id,
        displayName: definition.displayName,
        statisticFilters: definition.filters.map(f => ({
            code: f.code,
            displayName: f.displayName,
            type: f.type,
            required: f.required,
            editable: f.editable !== false,
            defaultValue: f.defaultValue,
            targetScreenId: f.targetScreen,
        })),
        widgets: definition.widgets,
    } as any) : null;

    const remove = async () => {
        if (!definition) return;
        const u = unwrap(await apiService.deleteStatistic(definition.id));
        setShowDelete(false);
        if (u.ok) {
            invalidateApps();
            navigate('/');
        } else {
            setError(u.message || null);
        }
    };

    const setDef = (patch: Partial<Definition>) =>
        setDefinition(d => (d ? {...d, ...patch} : d));

    const setFilter = (code: string, patch: Partial<BuilderFilter>) =>
        setDefinition(d => (d ? {
            ...d,
            filters: d.filters.map(f => (f.code === code ? {...f, ...patch} : f)),
        } : d));

    if (loading) {
        return (
            <div className="page-body">
                <div className="text-center py-5"><Spinner className="text-primary"/></div>
            </div>
        );
    }

    return (
        <div className="page-body">
            <div className="container-fluid py-3 d-flex flex-column" style={{height: 'calc(100vh - 70px)'}}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h2 className="mb-0">{definition ? definition.displayName : 'Nová štatistika'}</h2>
                    {definition && (
                        <div className="d-flex gap-2">
                            <Button variant="outline-primary" onClick={() => setShowPreview(true)}>Náhľad dát</Button>
                            <Button variant="primary" onClick={save} disabled={saving}>
                                {saving ? <Spinner size="sm"/> : 'Uložiť zmeny'}
                            </Button>
                            <Button variant="outline-danger" onClick={() => setShowDelete(true)}>Zmazať</Button>
                        </div>
                    )}
                </div>

                {notice && <div className="alert alert-success py-2">{notice}</div>}
                {error && <div className="alert alert-danger py-2" style={{whiteSpace: 'pre-line'}}>{error}</div>}
                {definition && !definition.sqlStatus.valid && (
                    <div className="alert alert-warning py-2">
                        Štatistika má momentálne technickú chybu — napíš asistentovi, čo má opraviť.
                        {definition.sqlStatus.error && <div className="small text-muted mt-1">{definition.sqlStatus.error}</div>}
                    </div>
                )}

                <div className="row g-3 flex-grow-1" style={{minHeight: 0}}>
                    {/* AI chat */}
                    <div className="col-lg-6 h-100">
                        <div className="card h-100 d-flex flex-column">
                            <div className="card-header py-2"><strong>AI asistent</strong></div>
                            <div className="card-body flex-grow-1 overflow-auto">
                                {definition?.chat.length ? (
                                    definition.chat.map((message, index) => (
                                        <div key={index} className={`mb-2 d-flex ${message.role === 'user' ? 'justify-content-end' : ''}`}>
                                            <div className={`border rounded p-2 ${message.role === 'user' ? 'bg-primary text-white' : 'bg-light'}`}
                                                 style={{maxWidth: '85%', whiteSpace: 'pre-line'}}>
                                                {message.text}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-muted">
                                        Napíš, čo má štatistika zobrazovať — napr. „Najviac predávané položky za mesiac"
                                        alebo „Počet úloh podľa zamestnanca s filtrom od–do".
                                    </div>
                                )}
                                {sending && <div className="text-muted small"><Spinner size="sm"/> Asistent pracuje…</div>}
                                <div ref={chatEndRef}/>
                            </div>
                            <div className="card-footer p-2">
                                <div className="d-flex gap-2">
                                    <Form.Control as="textarea" rows={2} value={chatInput}
                                                  placeholder={definition ? 'Čo mám zmeniť?' : 'Čo má štatistika zobrazovať?'}
                                                  onChange={e => setChatInput(e.target.value)}
                                                  onKeyDown={e => {
                                                      if (e.key === 'Enter' && !e.shiftKey) {
                                                          e.preventDefault();
                                                          send();
                                                      }
                                                  }}/>
                                    <Button variant="primary" onClick={send} disabled={sending}
                                            className="d-flex align-items-center justify-content-center align-self-end"
                                            style={{width: 48, height: 48, flexShrink: 0, padding: 0}}
                                            aria-label="Odoslať" title="Odoslať">
                                        {sending ? <Spinner size="sm"/> : <IconSend size={24}/>}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Definition — no SQL on purpose */}
                    <div className="col-lg-6 h-100">
                        {(
                            <div className="card h-100 d-flex flex-column">
                                <div className="card-header py-2"><strong>Definícia</strong></div>
                                <div className="card-body flex-grow-1 overflow-auto">
                                    <div className="row g-3 mb-3">
                                        <div className="col-md-9">
                                            <Form.Label>Názov {!definition && <span className="text-muted small">(prázdne doplní asistent)</span>}</Form.Label>
                                            <Form.Control value={definition ? definition.displayName : draft.displayName}
                                                          onChange={e => definition
                                                              ? setDef({displayName: e.target.value})
                                                              : setDraft(d => ({...d, displayName: e.target.value}))}/>
                                        </div>
                                        <div className="col-md-3">
                                            <Form.Label>Poradie</Form.Label>
                                            <Form.Control type="number" value={definition ? definition.sortOrder : draft.sortOrder}
                                                          onChange={e => {
                                                              const sortOrder = parseInt(e.target.value, 10) || 0;
                                                              definition ? setDef({sortOrder}) : setDraft(d => ({...d, sortOrder}));
                                                          }}/>
                                        </div>
                                        <div className="col-12">
                                            <Form.Label>Skupiny <span className="text-muted small">(bez výberu = vidia všetci)</span></Form.Label>
                                            <GroupsPicker all={userGroups}
                                                          selected={definition ? definition.userGroups : draft.userGroups}
                                                          onChange={ids => definition
                                                              ? setDef({userGroups: ids})
                                                              : setDraft(d => ({...d, userGroups: ids}))}/>
                                        </div>
                                        <div className="col-12">
                                            <Form.Label>Popis {!definition && <span className="text-muted small">(prázdne doplní asistent)</span>}</Form.Label>
                                            <Form.Control value={definition ? (definition.description ?? '') : draft.description}
                                                          onChange={e => definition
                                                              ? setDef({description: e.target.value})
                                                              : setDraft(d => ({...d, description: e.target.value}))}/>
                                        </div>
                                        {definition && (
                                            <WidgetList statisticId={definition.id} statisticName={definition.displayName}
                                                        widgets={definition.widgets} filters={previewScreen?.statisticFilters ?? []}
                                                        onDelete={setWidgetToDelete}
                                                        onChanged={updated => {
                                                            // only the widgets: unsaved edits of the statistic itself stay
                                                            setDefinition(current => current ? {...current, widgets: updated.widgets} : current);
                                                            invalidateApps();
                                                        }}/>
                                        )}
                                    </div>

                                    <Form.Label>Filtre <span className="text-muted small">(s týmito hodnotami sa štatistika otvorí; pridávajú sa cez asistenta)</span></Form.Label>
                                    {!definition || definition.filters.length === 0 ? (
                                        <div className="text-muted small">Štatistika nemá žiadne filtre.</div>
                                    ) : definition && (
                                        <div className="border rounded p-3" style={{backgroundColor: 'rgb(248, 249, 250)'}}>
                                            {definition.filters.map((filter, index) => (
                                                <div key={filter.code} className={index > 0 ? 'mt-3 pt-3 border-top' : ''}
                                                     data-e2e={`builder-filter:${filter.code}`}>
                                                    <div className="d-flex align-items-center gap-3 mb-1">
                                                        <Form.Control size="sm" value={filter.displayName} aria-label="Názov filtra"
                                                                      onChange={e => setFilter(filter.code, {displayName: e.target.value})}/>
                                                        <Form.Check type="switch" id={`filter-required-${filter.code}`} label="Povinný"
                                                                    className="text-nowrap mb-0" checked={filter.required}
                                                                    onChange={e => setFilter(filter.code, {required: e.target.checked})}/>
                                                        <Form.Check type="switch" id={`filter-editable-${filter.code}`} label="Môže meniť"
                                                                    title="Vypnuté = používateľ filter nezmení, vždy platí predvolená hodnota"
                                                                    className="text-nowrap mb-0" checked={filter.editable !== false}
                                                                    onChange={e => setFilter(filter.code, {editable: e.target.checked})}/>
                                                    </div>
                                                    <FilterDefaultInput filter={filter}
                                                                        onChange={defaultValue => setFilter(filter.code, {defaultValue})}/>
                                                    {filter.editable === false && !filter.defaultValue && (
                                                        <div className="text-warning small mt-1">
                                                            Filter sa nedá meniť, preto potrebuje predvolenú hodnotu.
                                                        </div>
                                                    )}
                                                    <FilterScreenLine filter={filter}
                                                                      onChange={screen => setFilter(filter.code, {targetScreen: screen.id, targetScreenName: screen.displayName})}/>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <ConfirmModal
                show={!!widgetToDelete}
                onHide={() => setWidgetToDelete(null)}
                onConfirm={async () => {
                    const widget = widgetToDelete;
                    setWidgetToDelete(null);
                    if (!definition || !widget) return;
                    const u = unwrap(await apiService.deleteStatisticWidget(definition.id, widget.id));
                    if (u.ok) {
                        applyDefinition(u.data);
                        invalidateApps();
                    } else {
                        setError(u.message || null);
                    }
                }}
                title="Zmazať widget?"
                message="Widget zmizne aj z domovských stránok používateľov, ktorí si ho pripli."
                confirmText="Zmazať"
                cancelText="Zrušiť"
                variant="danger"
            />

            {/* Data preview */}
            <Modal show={showPreview} onHide={() => setShowPreview(false)} size="xl" className="modal-blur">
                <Modal.Header closeButton>
                    <Modal.Title>Náhľad dát — {definition?.displayName}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {showPreview && previewScreen && <StatisticView screen={previewScreen} embedded/>}
                </Modal.Body>
            </Modal>

            {/* Delete confirm */}
            <Modal show={showDelete} onHide={() => setShowDelete(false)} centered className="modal-blur">
                <Modal.Header closeButton>
                    <Modal.Title>Zmazať štatistiku</Modal.Title>
                </Modal.Header>
                <Modal.Body>Naozaj chceš zmazať štatistiku „{definition?.displayName}"?</Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setShowDelete(false)}>Zrušiť</Button>
                    <Button variant="danger" onClick={remove}>Zmazať</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};
